import { computed, onMounted, onUnmounted, reactive, ref, toRaw, watch } from 'vue';
import { merge } from 'lodash';
import { Map as OlMap } from 'ol';
import type { Extent } from 'ol/extent';
import { pointerMove } from 'ol/events/condition';
import { Select as OlMapSelectInteraction } from 'ol/interaction';
import {
  MAP_CENTER_HUNGARY_3857,
  MAP_PROJECTION_3857,
  MAP_PROJECTION_4326,
  MAP_ZOOM_DEFAULT
} from '../models/map.model';
import type { Map, MapConfig, MapInfo, MapLayer, MapLayerGroup, VectorLayerZone } from '../models';
import {
  createEmptyExtent,
  generateColorInfo,
  generateMapInfoValues,
  getMapAutoExtent,
  isExtentEmpty,
  mergeExtents
} from '../utils';
import { MapCalculationMode } from '../enums';
import { createMapInfo, useMapInfo, useMapLayer } from '../composables';
import { useI18n } from 'vue-i18n';

export const createMap = (config: Partial<MapConfig> & { id: string }): Map => {
  const configuration = createMapConfig(config);
  const { t } = useI18n();

  return {
    id: configuration.id,
    loadTilesWhileAnimating: configuration.loadTilesWhileAnimating,
    loadTilesWhileInteracting: configuration.loadTilesWhileInteracting,
    projection: configuration.projection,
    center: configuration.center,
    zoom: configuration.zoom,
    mapSourceUrl: configuration.mapSourceUrl,
    attributions: configuration.attributions,
    mapSourcePreload: configuration.mapSourcePreload,
    layers: [],
    extent: [],
    fitExtent: undefined,
    fitDuration: configuration.fitDuration,
    fitPadding: configuration.fitPadding,
    zoneInteractionEnabled: false,
    calculationMode: configuration.calculationMode,
    mapInfoEnabled: configuration.mapInfoEnabled,
    mapInfos: [],
    connectedMaps: [],
    firstMapLayerLoaded: false,
    showId: configuration.showId,
    autoFitOnFirstLayerLoad: configuration.autoFitOnFirstLayerLoad,
    coordinatesEnabled: configuration.coordinatesEnabled,
    coordinatesProjection: configuration.coordinatesProjection,
    coordinatesPrecision: configuration.coordinatesPrecision,
    scaleLineEnabled: configuration.scaleLineEnabled,
    scaleLineUnits: configuration.scaleLineUnits,
    t
  };
};

export const useMap = (map: Map) => {
  const reactiveMap = reactive(map);

  const loadTilesWhileAnimating = computed({
    get: () => reactiveMap.loadTilesWhileAnimating,
    set: (val) => (reactiveMap.loadTilesWhileAnimating = val)
  });

  const loadTilesWhileInteracting = computed({
    get: () => reactiveMap.loadTilesWhileInteracting,
    set: (val) => (reactiveMap.loadTilesWhileInteracting = val)
  });

  const id = computed(() => reactiveMap.id);
  const projection = computed({ get: () => reactiveMap.projection, set: (val) => (reactiveMap.projection = val) });
  const center = computed({ get: () => reactiveMap.center, set: (val) => (reactiveMap.center = val) });
  const zoom = computed({ get: () => reactiveMap.zoom, set: (val) => (reactiveMap.zoom = val) });
  const mapSourceUrl = computed({
    get: () => reactiveMap.mapSourceUrl,
    set: (val) => (reactiveMap.mapSourceUrl = val)
  });
  const attributions = computed({
    get: () => reactiveMap.attributions,
    set: (val) => (reactiveMap.attributions = val)
  });
  const mapSourcePreload = computed({
    get: () => reactiveMap.mapSourcePreload,
    set: (val) => (reactiveMap.mapSourcePreload = val)
  });
  const extent = computed({ get: () => reactiveMap.extent, set: (val) => (reactiveMap.extent = val) });
  const fitExtent = computed({ get: () => reactiveMap.fitExtent, set: (val) => (reactiveMap.fitExtent = val) });
  const fitDuration = computed({ get: () => reactiveMap.fitDuration, set: (val) => (reactiveMap.fitDuration = val) });
  const fitPadding = computed({ get: () => reactiveMap.fitPadding, set: (val) => (reactiveMap.fitPadding = val) });
  const calculationMode = computed({
    get: () => reactiveMap.calculationMode,
    set: (val) => (reactiveMap.calculationMode = val)
  });
  const mapInfoEnabled = computed({
    get: () => reactiveMap.mapInfoEnabled,
    set: (val) => (reactiveMap.mapInfoEnabled = val)
  });
  const showId = computed({ get: () => reactiveMap.showId, set: (val) => (reactiveMap.showId = val) });
  const coordinatesEnabled = computed({
    get: () => reactiveMap.coordinatesEnabled,
    set: (val) => (reactiveMap.coordinatesEnabled = val)
  });
  const coordinatesProjection = computed({
    get: () => reactiveMap.coordinatesProjection,
    set: (val) => (reactiveMap.coordinatesProjection = val)
  });
  const coordinatesPrecision = computed({
    get: () => reactiveMap.coordinatesPrecision,
    set: (val) => (reactiveMap.coordinatesPrecision = val)
  });
  const scaleLineEnabled = computed({
    get: () => reactiveMap.scaleLineEnabled,
    set: (val) => (reactiveMap.scaleLineEnabled = val)
  });
  const scaleLineUnits = computed({
    get: () => reactiveMap.scaleLineUnits,
    set: (val) => (reactiveMap.scaleLineUnits = val)
  });
  const autoFitOnFirstLayerLoad = computed({
    get: () => reactiveMap.autoFitOnFirstLayerLoad,
    set: (val) => (reactiveMap.autoFitOnFirstLayerLoad = val)
  });

  const zoneInteractionEnabled = computed({
    get: () => reactiveMap.zoneInteractionEnabled,
    set: (val) => (reactiveMap.zoneInteractionEnabled = val)
  });

  const layers = computed(() => reactiveMap.layers);

  const mapExtent = computed(() =>
    layers.value.reduce((prev: Extent, cur) => mergeExtents(prev, cur.activeSubLayer.extent || []), [])
  );
  const mapInfos = computed(() => reactiveMap.mapInfos);
  const mergedMapInfos = computed(() => {
    const result: MapInfo[] = [];

    mapInfos.value.forEach((mapInfo) => {
      if (!mapInfo.enabled || result.some((mi) => mi.layerType === mapInfo.layerType)) return;
      result.push(mapInfo);
    });

    return result;
  });
  const mapInfosForCalculationMode = computed(() =>
    calculationMode.value === MapCalculationMode.ISOLATED ? mapInfos.value : mergedMapInfos.value
  );

  const addLayer = async (layer: MapLayerGroup, position?: number) => {
    layer.map = map;

    if (position === undefined) {
      position = layers.value.length;
    }

    layers.value.splice(position, 0, layer);

    for (const subLayer of layer.subLayers) {
      await addMapInfoForSubLayer(subLayer);
    }

    updateZIndexes();
  };

  const getLayer = (layerId: string) => {
    return layers.value.find((layer) => layer.id === layerId);
  };

  const removeLayer = (layerId: string) => {
    const layer = getLayer(layerId);
    if (!layer) return;
    layer.map = undefined;

    const index = layers.value.findIndex((layer) => layer.id === layerId);
    if (index > -1) {
      if (reactiveMap.mapInfoEnabled) {
        const subLayerIds = layer.subLayers.map((subLayer) => subLayer.id);
        subLayerIds.forEach((subLayerId) => removeMapInfo(subLayerId));
      }

      layers.value.splice(index, 1);
    }

    updateZIndexes();
  };

  const clearLayers = () => {
    layers.value.splice(0);

    if (reactiveMap.mapInfoEnabled) {
      mapInfos.value.splice(0);
      reactiveMap.mapInfos.splice(0);
    }
  };

  const addSubLayer = async (layerId: string, subLayer: MapLayer) => {
    const layer = getLayer(layerId);
    if (!layer) return;

    subLayer.group = layer;
    layer.subLayers.push(subLayer);

    await addMapInfoForSubLayer(subLayer);
  };

  const getSubLayer = (layerId: string, subLayerId: string) => {
    const layer = getLayer(layerId);
    if (!layer) return undefined;

    return layer.subLayers.find((subLayer) => subLayer.id === subLayerId);
  };

  const switchSubLayer = (layerId: string, subLayerId: string) => {
    const layer = getLayer(layerId);
    if (!layer) return;

    const subLayer = getSubLayer(layerId, subLayerId);
    if (!subLayer) return;

    const enabled = layer.activeSubLayer.enabled;
    const layerMapInfoEnabled = layer.activeSubLayer.mapInfo.enabled;
    layer.activeSubLayer.enabled = false;
    layer.activeSubLayer.mapInfo.enabled = false;

    if (reactiveMap.mapInfoEnabled) {
      disableMapInfo(layer.activeSubLayer.id);
    }

    updateZIndexes();
    layer.activeSubLayer = subLayer;
    layer.activeSubLayer.enabled = enabled;
    layer.activeSubLayer.mapInfo.enabled = layerMapInfoEnabled;

    if (reactiveMap.mapInfoEnabled) {
      enableMapInfo(layer.activeSubLayer.id);
    }
  };

  const fitExtentInView = (extent?: Extent) => {
    fitExtent.value = toRaw(extent);
  };

  const fitMapExtentInView = (auto = false) => {
    fitExtentInView(auto ? [] : mapExtent.value);
  };

  const fitMapExtentByLayers = () => {
    let extent = createEmptyExtent();

    layers.value.forEach((layer) => {
      extent = mergeExtents(extent, layer.activeSubLayer.extent || []);
    });

    fitExtentInView(extent);
  };

  const enableZoneInteraction = () => {
    zoneInteractionEnabled.value = true;
  };

  const disableZoneInteraction = () => {
    zoneInteractionEnabled.value = false;
  };

  const getMergedValuesByLayerType = (type: string) => {
    const result: number[] = [];

    layers.value
      .filter((layer) => layer.activeSubLayer.type === type)
      .forEach((layer) => {
        layer.activeSubLayer.data.values.forEach((value) => result.push(value));
      });

    reactiveMap.connectedMaps.forEach((connectedMap) => {
      connectedMap.layers
        .filter((layer) => layer.activeSubLayer.type === type)
        .forEach((layer) => {
          layer.activeSubLayer.data.values.forEach((value) => result.push(value));
        });
    });

    return result;
  };

  const addMapInfoForSubLayer = async (subLayer: MapLayer) => {
    if (!reactiveMap.mapInfoEnabled) return;

    const colorMap = subLayer.data.colorMap;
    if (colorMap && !subLayer.mapInfo.doNotGenerate) {
      await subLayer.loadingPromise;

      const values =
        calculationMode.value === MapCalculationMode.ISOLATED
          ? subLayer.data.values
          : getMergedValuesByLayerType(subLayer.type);

      const colorInfo = subLayer.generateColorInfo
        ? await subLayer.generateColorInfo(values, colorMap)
        : await generateColorInfo(subLayer.mapLayerType, values, colorMap);

      const enabled = subLayer === subLayer.group?.activeSubLayer && subLayer.mapInfo.enabled;

      const mapInfo = createMapInfo({
        layerType: subLayer.type,
        layerId: subLayer.id,
        title: subLayer.mapInfo.titleFn(subLayer),
        subtitle: subLayer.mapInfo.subtitleFn(subLayer, map.t),
        colorBar: subLayer.mapInfo.colorBar,
        values: generateMapInfoValues(subLayer, colorInfo, subLayer.mapInfo.valueFn),
        enabled
      });

      addMapInfo(mapInfo);
    }
  };

  const addMapInfo = (mapInfo: MapInfo) => {
    reactiveMap.mapInfos.push(mapInfo);
    updateMapInfos().then();
  };

  const removeMapInfo = (layerId: string) => {
    const index = reactiveMap.mapInfos.findIndex((mapInfo) => mapInfo.layerId === layerId);

    if (index > -1) {
      reactiveMap.mapInfos.splice(index, 1);
    }
  };

  const updateMapInfos = async () => {
    if (!mapInfoEnabled.value) return;

    const enabledLayers = layers.value.filter((layer) => layer.activeSubLayer.enabled);

    for (const layer of enabledLayers) {
      const subLayer = layer.activeSubLayer;
      const { reactiveLayer } = useMapLayer(subLayer);
      const subLayerId = reactiveLayer.id;
      const mapInfo = getMapInfoByLayerId(subLayerId);
      const colorMap = reactiveLayer.data.colorMap;
      if (!mapInfo || !colorMap) return;

      const values =
        calculationMode.value === MapCalculationMode.ISOLATED
          ? subLayer.data.values
          : getMergedValuesByLayerType(subLayer.type);
      const colorInfo = subLayer.generateColorInfo
        ? await subLayer.generateColorInfo(values, colorMap)
        : await generateColorInfo(reactiveLayer.mapLayerType, values, colorMap);

      const { reactiveMapInfo } = useMapInfo(mapInfo);
      reactiveMapInfo.title = subLayer.mapInfo.titleFn(reactiveLayer);
      reactiveMapInfo.subtitle = subLayer.mapInfo.subtitleFn(reactiveLayer, map.t);
      reactiveMapInfo.values = generateMapInfoValues(reactiveLayer, colorInfo, reactiveLayer.mapInfo.valueFn);
    }
  };

  const getMapInfoByLayerId = (layerId: string) => {
    return reactiveMap.mapInfos.find((mapInfo) => mapInfo.layerId === layerId);
  };

  const enableMapInfo = (layerId: string) => {
    const mapInfo = getMapInfoByLayerId(layerId);
    if (!mapInfo) return;

    const { enabled } = useMapInfo(mapInfo);
    enabled.value = true;
    updateMapInfos().then();
  };

  const disableMapInfo = (layerId: string) => {
    const mapInfo = getMapInfoByLayerId(layerId);
    if (!mapInfo) return;

    const { enabled } = useMapInfo(mapInfo);
    enabled.value = false;
    updateMapInfos().then();
  };

  const updateZIndexes = () => {
    const activeLayers = reactiveMap.layers.filter((layer) => layer.activeSubLayer.enabled);

    activeLayers.forEach((layer, i) => {
      layer.zIndex = activeLayers.length - i;
    });
  };

  return {
    id,
    reactiveMap,
    loadTilesWhileAnimating,
    loadTilesWhileInteracting,
    projection,
    center,
    zoom,
    mapSourceUrl,
    attributions,
    mapSourcePreload,
    extent,
    fitExtent,
    fitDuration,
    fitPadding,
    zoneInteractionEnabled,
    layers,
    mapExtent,
    calculationMode,
    mapInfoEnabled,
    mapInfos,
    mergedMapInfos,
    mapInfosForCalculationMode,
    showId,
    autoFitOnFirstLayerLoad,
    coordinatesEnabled,
    coordinatesProjection,
    coordinatesPrecision,
    scaleLineEnabled,
    scaleLineUnits,
    addLayer,
    getLayer,
    removeLayer,
    clearLayers,
    addSubLayer,
    getSubLayer,
    switchSubLayer,
    fitExtentInView,
    fitMapExtentInView,
    fitMapExtentByLayers,
    enableZoneInteraction,
    disableZoneInteraction,
    getMergedValuesByLayerType,
    addMapInfo,
    removeMapInfo,
    updateMapInfos,
    getMapInfoByLayerId,
    updateZIndexes
  };
};

export const useMapComponent = (map: Map) => {
  const composedMap = useMap(map);
  const {
    reactiveMap,
    autoFitOnFirstLayerLoad,
    layers,
    zoneInteractionEnabled,
    fitExtent,
    fitDuration,
    fitPadding,
    updateZIndexes
  } = composedMap;

  const mapRef = ref<any>(null);
  const mapKey = ref(0);

  const zoneSelectInteraction = new OlMapSelectInteraction({
    condition: pointerMove
  });

  zoneSelectInteraction.on('select', (event) => {
    event.deselected.forEach((feature) => {
      const zone = feature.get('zone') as VectorLayerZone;
      if (!zone) return;
      zone.selected = false;
      zone.customData.selected = false;
    });

    event.selected.forEach((feature) => {
      const zone = feature.get('zone') as VectorLayerZone;
      if (!zone) return;
      zone.selected = true;
      zone.customData.selected = true;
    });
  });

  const getOlMap = () => {
    return mapRef.value?.map as OlMap;
  };

  const fitExtentInView = (extent?: Extent) => {
    const map = getOlMap();

    if (!extent || !extent.length) {
      extent = getMapAutoExtent(map);
    }

    if (isExtentEmpty(extent)) return;

    map.getView().fit(extent, {
      size: map.getSize(),
      duration: fitDuration.value,
      padding: [fitPadding.value, fitPadding.value, fitPadding.value, fitPadding.value]
    });
  };

  onMounted(() => {
    mapKey.value = mapKey.value + 1;
    reactiveMap.firstMapLayerLoaded = false;
    fitExtentInView(fitExtent.value);

    if (zoneInteractionEnabled.value) {
      getOlMap()?.addInteraction(zoneSelectInteraction);
    }
  });

  onUnmounted(() => {
    getOlMap()?.removeInteraction(zoneSelectInteraction);
  });

  watch(mapRef, () => {
    if (mapRef.value) {
      if (zoneInteractionEnabled.value) {
        getOlMap()?.addInteraction(zoneSelectInteraction);
      }
    }
  });

  watch(zoneInteractionEnabled, (enabled) => {
    if (enabled) getOlMap()?.addInteraction(zoneSelectInteraction);
    else getOlMap()?.removeInteraction(zoneSelectInteraction);
  });

  watch(fitExtent, (extent) => {
    if (extent && extent.length === 1 && extent[0] === 0) {
      return;
    }

    fitExtentInView(extent);
    fitExtent.value = [0];
  });

  watch(
    () => reactiveMap.firstMapLayerLoaded,
    () => {
      if (autoFitOnFirstLayerLoad.value) {
        fitExtentInView();
      }
    },
    { deep: true }
  );

  watch(layers, () => {
    updateZIndexes();

    layers.value.forEach((layer) => {
      const oldMap = layer.map;
      if (oldMap && oldMap !== map) {
        const composedOldMap = useMap(oldMap);
        const mapInfo = composedOldMap.getMapInfoByLayerId(layer.activeSubLayer.id);
        if (mapInfo) {
          composedOldMap.removeMapInfo(layer.activeSubLayer.id);
          composedMap.addMapInfo(mapInfo);
        }
      }

      layer.map = map;

      const { fitMapExtentByLayers: fitNewMap } = useMap(layer.map);
      fitNewMap();
    });
  });

  return {
    ...composedMap,
    mapRef,
    mapKey,
    getOlMap
  };
};

export const createMapConfig = (config: Partial<MapConfig> & { id: string }): MapConfig => {
  return merge(
    {
      projection: MAP_PROJECTION_3857,
      center: MAP_CENTER_HUNGARY_3857,
      zoom: MAP_ZOOM_DEFAULT,
      loadTilesWhileAnimating: true,
      loadTilesWhileInteracting: true,
      mapSourcePreload: Infinity,
      zoneInteractionEnabled: false,
      fitDuration: 250,
      fitPadding: 50,
      mapInfoEnabled: false,
      calculationMode: MapCalculationMode.ISOLATED,
      showId: false,
      autoFitOnFirstLayerLoad: true,
      coordinatesEnabled: 'hover',
      coordinatesProjection: MAP_PROJECTION_4326,
      coordinatesPrecision: 4,
      scaleLineEnabled: true,
      scaleLineUnits: 'metric'
    },
    config
  );
};
